import { useEffect, useRef } from 'react';
import { Region } from 'wavesurfer.js/src/plugin/regions';

import { MIN_CELL_DUR } from 'constants/params';
import { RootState } from 'store/store';
import { useAppSelector, useAppDispatch, useAppStore } from 'store/store-hooks';
import { Path, useLocation, getPath } from 'components/_hooks/use-router';
import { roundTime, convertTransition, isNull } from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { SetRegionsParams, setRegions, getMarkerNodes } from 'components/app/audio-track/_mediator/audio-track.helper';
import { getMarkersEnabledRange } from 'components/app/_mediator/range-markers/range-markers.helper';
import { handleMarkers, setSelectedMarkers } from 'components/app/editor/_mediator/editor.slice';
import { isSmallRegion, sortMarkers, getCursorCell } from './editor-markers.helper';
import { IntroKeys } from 'components/app/intro/intro-keys';

import audioTrackStyles from 'components/app/audio-track/_audio-track.module.scss';
import wsHeaderStyles from 'components/app/audio-track/ws-header/_ws-header.module.scss';
import editorBackupStyles from 'components/app/editor/editor-actions/_editor-actions.module.scss';

export const useMarkers = (ws: WaveSurfer) => {
  const { path } = useLocation();
  const cells = useAppSelector((state) => state.editor.cells);
  const selectedMarkers = useAppSelector((state) => state.editor.selectedMarkers);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const firstRangeMarkerSelected = useRef<number | null>(null);
  const firstRangeMarkerCursorPointer = useRef<number | null>(null);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  /* helpers */
  const clearFirstRangeMarkerCursorPointer = (condition: boolean) =>
    condition && (firstRangeMarkerCursorPointer.current = null);

  const clearFirstRangeMarkerSelected = (condition: boolean) => condition && (firstRangeMarkerSelected.current = null);
  const isNumberValue = (value: number | null) => Number.isInteger(value);

  const setRegionsClass = (selectedMarkers?: number[]) => {
    const { storyConfig, markers } = selectData(store.getState());

    if (!storyConfig) return;

    const markerNodes = getMarkerNodes(ws);
    const possibleClasses = [`region--selected`, `region--out-of-range`];

    markerNodes.forEach((el, index) => {
      const enabledRange = getMarkersEnabledRange(storyConfig, markers?.length ?? 0, 'marker');

      const currentClassList = (el as HTMLElement)?.classList;

      possibleClasses.forEach((currentClass) => currentClassList?.remove(audioTrackStyles[currentClass]));

      if (!enabledRange.includes(index) && path === Path.storyboard)
        currentClassList?.add(audioTrackStyles[possibleClasses[1]]);

      if (index === 0) {
        (el as HTMLElement).setAttribute('data-intro', IntroKeys.firstBeatMarker.toString());
      }
    });

    if (selectedMarkers)
      selectedMarkers.forEach(
        (id) => (markerNodes[id] as HTMLElement)?.classList?.add(audioTrackStyles[possibleClasses[0]])
      );
  };

  const isClickOutsideArea = (areaAxs: number, clickAxs: number) => areaAxs < clickAxs || clickAxs < 0;

  const overrideRegionsOnDrag = () => {
    const { selectedMarkers } = selectData(store.getState());

    const regionsList = Object.values(ws.regions.list);
    const selectedRegionsList = regionsList.filter((region) => selectedMarkers.includes(Number(region.id)));

    if (selectedRegionsList && selectedRegionsList.length)
      selectedRegionsList.forEach(
        (currentRegion) =>
          (currentRegion.onDrag = (delta: number) => {
            const { heldKey } = selectData(store.getState());

            if (heldKey) return;

            const movingForward = delta >= 0;

            const transitionTime = storyConfig?.transitionTime ? convertTransition(storyConfig.transitionTime) : 0;
            const trackEdge = movingForward ? ws.getDuration() : 0;

            selectedRegionsList.forEach((currentRegion) => {
              const appropriateDelta = selectedRegionsList.every((region) =>
                movingForward ? region.start + transitionTime <= trackEdge : region.start >= trackEdge
              )
                ? delta
                : delta + (movingForward ? -MIN_CELL_DUR : MIN_CELL_DUR);

              currentRegion.update({
                id: currentRegion.id,
                start: currentRegion.start + appropriateDelta,
                end: currentRegion.end + appropriateDelta,
              });
            });
          })
      );
  };

  const unselectMarkersOnAnyPlaceClick = (event: MouseEvent) => {
    const { heldKey, selectedMarkers } = selectData(store.getState());

    const path = event.composedPath && event.composedPath();
    const target = (event?.target as HTMLElement) ?? null;
    const markersSelected = selectedMarkers.length;
    const isTargetRegion = target?.tagName?.toLowerCase() === 'region';
    const skipEventListener =
      path &&
      path.some((element) => {
        const classList = (element as HTMLElement)?.classList ?? [];
        return [...classList].some(
          (e) => e.includes(wsHeaderStyles['WsHeader']) || e.includes(editorBackupStyles['EditorActions__rightCol'])
        );
      });
    const isClickOutside =
      isClickOutsideArea(window.innerWidth, event.clientX) || isClickOutsideArea(window.innerHeight, event.clientY);

    if (!target || (isTargetRegion && heldKey) || skipEventListener || isClickOutside) return;

    if (markersSelected) {
      dispatch(setSelectedMarkers([]));
      setUndraggableRegions();
    }

    clearFirstRangeMarkerSelected(true);
    clearFirstRangeMarkerCursorPointer(!isTargetRegion);
  };

  const setUndraggableRegions = (options?: { excludedRegions?: number[] }) => {
    const { markers, cells, transition } = selectData(store.getState());
    const { excludedRegions } = options ?? {};

    if (!cells || transition === undefined || !markers) return;

    setRegions({
      ws,
      markers,
      ranges: cells.duration,
      transitionTime: transition,
      ...(excludedRegions
        ? { dragCondition: (index: number) => excludedRegions.includes(index) }
        : { draggable: false }),
    });

    if (excludedRegions) {
      const markerNodes = getMarkerNodes(ws);

      overrideRegionsOnDrag();

      excludedRegions.forEach((id) =>
        (markerNodes[id] as HTMLElement).classList.add(audioTrackStyles['region--selected'])
      );
    }
  };

  const regionRangeToggled = (marker: Region, setRegionsParams: SetRegionsParams) => {
    const firstRangeMarkerID = firstRangeMarkerSelected.current;
    const rangeMarkerPointerID = firstRangeMarkerCursorPointer.current;
    const { heldKey } = selectData(store.getState());

    if (!isNumberValue(firstRangeMarkerID)) firstRangeMarkerSelected.current = Number(marker.id);
    if ((isNumberValue(firstRangeMarkerID) || isNumberValue(rangeMarkerPointerID)) && heldKey === 'Shift') {
      const { markers } = selectData(store.getState());

      const appropriateRangeIdentifier = rangeMarkerPointerID ?? firstRangeMarkerID;

      const markersRange = markers
        ?.map((_, index) => {
          const isRangeForward = Number(marker.id) >= appropriateRangeIdentifier!;

          let neededMarkerCondition = false;

          neededMarkerCondition = isRangeForward
            ? index >= appropriateRangeIdentifier! && index <= Number(marker.id)
            : index <= appropriateRangeIdentifier! && index >= Number(marker.id);

          return neededMarkerCondition ? index : null;
        })
        ?.filter((marker_item) => !isNull(marker_item)) as number[];

      setRegions({ ...setRegionsParams, dragCondition: (regionID) => markersRange.includes(regionID) });
      dispatch(setSelectedMarkers(markersRange));
      return true;
    }

    return false;
  };

  const onRegionClick = (marker: Region, e: Event) => {
    const { markers, duration, cells, transition, heldKey, selectedMarkers } = selectData(store.getState());

    if (!heldKey) firstRangeMarkerCursorPointer.current = Number(marker.id);
    if (!heldKey || getPath() === Path.finalcuts) return;
    if (!markers || !duration || !cells || transition === undefined) return;

    e.stopPropagation();

    const selectedMarkerID = Number(marker.id);
    const cursorPointerMarkerID = isNumberValue(firstRangeMarkerCursorPointer.current)
      ? firstRangeMarkerCursorPointer.current
      : null;

    const commonSetRegionsParams = {
      ws,
      markers,
      ranges: cells.duration,
      transitionTime: transition,
    };

    if (regionRangeToggled(marker, commonSetRegionsParams)) return;

    firstRangeMarkerSelected.current = Number(marker.id);

    if (selectedMarkers.includes(selectedMarkerID)) {
      const filteredMarkers = [...selectedMarkers].filter((el) => el !== selectedMarkerID);

      clearFirstRangeMarkerCursorPointer(heldKey === 'Control');
      dispatch(setSelectedMarkers(filteredMarkers));
      setRegions({
        ...commonSetRegionsParams,
        dragCondition: (index: number) => filteredMarkers.includes(index),
      });

      return;
    }

    const additionalMarkers = isNumberValue(cursorPointerMarkerID) ? [Number(cursorPointerMarkerID)] : [];
    const updatedMarkers = [...new Set([...selectedMarkers, selectedMarkerID, ...additionalMarkers])];

    dispatch(setSelectedMarkers(updatedMarkers));
    setRegions({
      ...commonSetRegionsParams,
      dragCondition: (index: number) => updatedMarkers.includes(index),
    });
    clearFirstRangeMarkerCursorPointer(heldKey === 'Control');
  };

  const onRegionDragEnd = () => {
    const { markers, duration, cells, transition, heldKey, selectedMarkers, storyConfig } = selectData(
      store.getState()
    );

    const regionsList = Object.values(ws.regions.list);
    const selectedRegionsList = regionsList.filter((region) => selectedMarkers.includes(Number(region.id)));

    if (!markers || !duration || !cells || transition === undefined || !selectedRegionsList || heldKey || !storyConfig)
      return;

    const unmovedMarkers = [...markers].filter((_, index) => !selectedMarkers.includes(index));
    const updatedMarkers = sortMarkers([
      ...unmovedMarkers,
      ...selectedRegionsList.map((region) => roundTime(region.start)),
    ]);

    const isValid = selectedRegionsList.every((region) => {
      region.data.dragging = 'true';
      const time = roundTime(region.start);
      return !isSmallRegion(time, getCursorCell(time, unmovedMarkers), unmovedMarkers, duration);
    });

    if (!isValid) {
      Error.tooSmallRegion();
      setUndraggableRegions({ excludedRegions: selectedMarkers });
      setRegionsClass(selectedMarkers);
      return;
    }

    const updatedSelectedMarkers = regionsList
      .map((region) => {
        const dragging = region.data.dragging === 'true';
        delete region.data.dragging;
        return updatedMarkers.indexOf(roundTime(region.start)) !== -1 && dragging
          ? updatedMarkers.indexOf(roundTime(region.start))
          : null;
      })
      .filter((marker) => !isNull(marker)) as number[];

    dispatch(
      handleMarkers({
        data: { markers: updatedMarkers },
        backupParams: { title: 'DnD Markers', selectedMarkers: [selectedMarkers, updatedSelectedMarkers] },
      })
    );
  };

  useEffect(() => {
    dispatch(setSelectedMarkers([]));
  }, [path]);

  useEffect(() => {
    //TODO: I've been looking for the better solution for a while, but with no result. Find the better solution in future!
    setTimeout(() => {
      const { selectedMarkers } = selectData(store.getState());
      setUndraggableRegions({ excludedRegions: selectedMarkers });
      setRegionsClass(selectedMarkers);
    }, 0);
  }, [cells, storyConfig?.transitionTime]);

  useEffect(() => {
    overrideRegionsOnDrag();
    setRegionsClass(selectedMarkers);
  }, [selectedMarkers, storyConfig?.startMarkerId, storyConfig?.finishMarkerId]);

  /* listeners */

  useEffect(() => {
    ws.on('region-click', onRegionClick);
    ws.on('region-update-end', onRegionDragEnd);
    window.addEventListener('click', unselectMarkersOnAnyPlaceClick);

    return () => {
      ws.un('region-update-end', onRegionDragEnd);
      ws.un('region-click', onRegionClick);
      window.removeEventListener('click', unselectMarkersOnAnyPlaceClick);
    };
  }, []);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { duration } = state.audio;
  const { markers, cells, selectedMarkers, storyConfig } = state.editor;
  const { heldKey } = state.app;
  return {
    duration,
    storyConfig: storyConfig,
    transition: storyConfig?.transitionTime,
    selectedMarkers,
    markers,
    cells,
    heldKey,
  };
};
